import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Loader2, Download, ExternalLink } from 'lucide-react'
import { useAdminTickets } from '@/hooks/useNaiDesk'
import api from '@/lib/api'

const STATUS_LABELS: Record<string, string> = {
  open: 'Aberto', in_progress: 'Em Andamento', forwarded: 'Encaminhado',
  waiting_client: 'Aguardando', resolved: 'Resolvido', closed: 'Fechado',
}

export default function TicketsAdmin() {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [type, setType] = useState('')
  const { data, isLoading } = useAdminTickets({ q, status, type })

  const exportCsv = async () => {
    const r = await api.get('/v1/portal/tickets/admin/export', { params: { status }, responseType: 'blob' })
    const url = URL.createObjectURL(r.data)
    const a = document.createElement('a'); a.href = url; a.download = 'nai-desk-tickets.csv'; a.click()
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Chamados — Admin</h1>
          <p className="text-sm text-gray-500">Visão completa de todos os chamados do portal</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold">
          <Download className="h-4 w-4" />Exportar CSV
        </button>
      </header>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <option value="">Todos os status</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={type} onChange={e => setType(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <option value="">Todos os tipos</option>
          <option value="Dúvidas">Dúvidas</option>
          <option value="Suporte">Suporte</option>
          <option value="Melhoria">Melhoria</option>
          <option value="Atendimento">Atendimento</option>
        </select>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-600 dark:text-gray-400">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-left">Assunto</th>
              <th className="px-4 py-3 text-left">Tipo / Módulo</th>
              <th className="px-4 py-3 text-left">SDP</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Data</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="px-4 py-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {data?.map(t => (
              <tr key={t.id} className="border-t border-gray-100 dark:border-gray-800">
                <td className="px-4 py-3 font-mono text-gray-500">#{t.ticket_number}</td>
                <td className="px-4 py-3 text-xs">{t.user_name}<br /><span className="text-gray-500">{t.user_email}</span></td>
                <td className="px-4 py-3 max-w-xs truncate">{t.subject}</td>
                <td className="px-4 py-3 text-xs">{t.type}<br /><span className="text-gray-500">{t.module || '—'}</span></td>
                <td className="px-4 py-3 text-xs font-mono">{t.sdp_ticket_id ? `#${t.sdp_ticket_id}` : '—'}</td>
                <td className="px-4 py-3">{STATUS_LABELS[t.status] || t.status}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{new Date(t.created_at).toLocaleDateString('pt-BR')}</td>
                <td className="px-4 py-3"><Link to={`/nai-desk/admin/tickets/${t.id}`} className="text-blue-600 hover:underline inline-flex items-center gap-1 text-xs"><ExternalLink className="h-3 w-3" />Abrir</Link></td>
              </tr>
            ))}
            {data && data.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Nenhum chamado.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  )
}
